import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { BiData, PureBiData } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import StandardMenu from '@wix/da-ds/pkg/Dropdown/MenuPreset/StandardMenu';
import { getDeviationResourceType } from '../../../../DeviationViews/_helpers';
import { useVerification } from '../../../../VerificationPopup/withVerification';
import { ActionBarContext } from '../../../ActionBarContext';
import { StandardDeviationElementPlacement } from '../../../ElementPlacementContext/variants/standard/types';
import { useElementPlacementForArea } from '../../../ElementPlacementContext/utils';
import { StandardDeviationElementPlacementContext } from '../../../ElementPlacementContext/variants/standard/context';
import { useFreeDownloadAction, usePremiumDownloadAction } from '../../DownloadButton/hooks';
import { useCopyDeviationLinkClick } from '../../../../utils/hooks/useCopyLinkClick';
import { AwardBadgeSource } from '../../../../coreBadge/types';
import { URLS } from '@wix/da-url';
import s from './MoreMenuContent.scss';
/**
 * Takes an array of actions that show popups and returns them with their dropdown entry's `onClick` callback
 * altered to call the supplied {@link callback} when executed.
 */ function withExtraCallbackInActionOnClick(actionsWithPopups, callback) {
    return Object.entries(actionsWithPopups).reduce((accumulator, [key, value])=>_object_spread_props(_object_spread({}, accumulator), {
            [key]: value ? _object_spread_props(_object_spread({}, value), {
                action: _object_spread_props(_object_spread({}, value.action), {
                    onClick: (e)=>{
                        value.action.onClick && value.action.onClick(e);
                        callback();
                    }
                })
            }) : value
        }), {});
}
// This is used only on desktop
const MoreMenuContent = ({ deviation, isOwner, closeTooltip = ()=>null, showReportModal, shareDeviationToPost, showAwardBadgePopup, onCommentClick, onUnpublishClick, actionsWithPopups })=>{
    var _deviation_author, _deviation_extended_pcp, _deviation_extended, _actionsWithPopupsAndAugmentedOnClick_addToGroup, _actionsWithPopupsAndAugmentedOnClick_privateCollection;
    const { t } = useTranslation();
    const actionBarType = useContext(ActionBarContext);
    const { restrictToVerifiedAndLoggedIn } = useVerification();
    const { onCopyLinkClick } = useCopyDeviationLinkClick(deviation);
    const elementPlacementMap = useElementPlacementForArea(actionBarType === 'primary' ? StandardDeviationElementPlacement.PrimaryBarMenu : StandardDeviationElementPlacement.SecondaryBarMenu, StandardDeviationElementPlacementContext);
    const freeDownloadAction = useFreeDownloadAction(deviation, isOwner);
    const premiumDownloadAction = usePremiumDownloadAction(deviation, isOwner);
    const actionsWithPopupsAndAugmentedOnClick = withExtraCallbackInActionOnClick(actionsWithPopups, closeTooltip);
    const handleCommentClick = useCallback(()=>{
        onCommentClick(deviation);
    }, [
        onCommentClick,
        deviation
    ]);
    const handleUnpublishClick = useCallback(()=>{
        onUnpublishClick({
            deviation
        });
        closeTooltip();
    }, [
        onUnpublishClick,
        closeTooltip,
        deviation
    ]);
    var _deviation_author_useridUuid;
    return /*#__PURE__*/ React.createElement(StandardMenu, {
        className: s['menu'],
        id: "actionbar-more-menu",
        items: [
            elementPlacementMap.comment && {
                value: 'comment',
                label: t('actionbar.comment.label'),
                onClick: handleCommentClick,
                biData: BiData({
                    evid: 105,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviation.deviationId,
                    creatorid: (_deviation_author_useridUuid = (_deviation_author = deviation.author) === null || _deviation_author === void 0 ? void 0 : _deviation_author.useridUuid) !== null && _deviation_author_useridUuid !== void 0 ? _deviation_author_useridUuid : ''
                })
            },
            elementPlacementMap.premiumDownload && premiumDownloadAction.hasAction && {
                value: 'download',
                label: t('actionbar.premiumDownload'),
                link: premiumDownloadAction.href,
                onClick: ()=>{
                    premiumDownloadAction.onClick && premiumDownloadAction.onClick();
                    closeTooltip();
                },
                biData: PureBiData({
                    evid: 350,
                    ispaiddownload: 1,
                    deviationid: deviation.deviationId,
                    sectionname: 'action_bar'
                })
            },
            elementPlacementMap.freeDownload && freeDownloadAction.hasAction && {
                value: 'download',
                label: t('actionbar.freeDownload'),
                link: freeDownloadAction.href,
                onClick: ()=>{
                    freeDownloadAction.onClick && freeDownloadAction.onClick();
                    closeTooltip();
                },
                biData: PureBiData({
                    evid: 350,
                    ispaiddownload: 0,
                    deviationid: deviation.deviationId,
                    sectionname: 'action_bar'
                })
            },
            elementPlacementMap.viewOrderHistory && {
                value: 'viewOrderHistory',
                label: t('actionbar.viewOrderHistory'),
                link: `${URLS.CUSTOMER_DASHBOARD}?subproductid=${(_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_pcp = _deviation_extended.pcp) === null || _deviation_extended_pcp === void 0 ? void 0 : _deviation_extended_pcp.subproductId}`
            },
            (_actionsWithPopupsAndAugmentedOnClick_addToGroup = actionsWithPopupsAndAugmentedOnClick.addToGroup) === null || _actionsWithPopupsAndAugmentedOnClick_addToGroup === void 0 ? void 0 : _actionsWithPopupsAndAugmentedOnClick_addToGroup.action,
            elementPlacementMap.awardBadge && {
                value: 'awardBadge',
                label: t('actionbar.awardBadge'),
                onClick: ()=>{
                    showAwardBadgePopup(deviation, AwardBadgeSource.ACTIONBAR_MORE);
                    closeTooltip();
                },
                biData: BiData({
                    evid: 320,
                    sectionname: 'deviation',
                    typeid: deviation.typeId,
                    itemid: deviation.deviationId
                })
            },
            (_actionsWithPopupsAndAugmentedOnClick_privateCollection = actionsWithPopupsAndAugmentedOnClick.privateCollection) === null || _actionsWithPopupsAndAugmentedOnClick_privateCollection === void 0 ? void 0 : _actionsWithPopupsAndAugmentedOnClick_privateCollection.action,
            elementPlacementMap.share && {
                value: 'copyLink',
                label: t('actionbar.copyLink'),
                onClick: onCopyLinkClick,
                biData: BiData({
                    evid: 355,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviation.deviationId,
                    deviationid: deviation.deviationId
                })
            },
            elementPlacementMap.share && {
                value: 'shareToPost',
                label: t('actionbar.shareToPost'),
                onClick: restrictToVerifiedAndLoggedIn(ReferralType.SHARE_TO_POST, 'submitstatus', ()=>shareDeviationToPost(deviation)),
                biData: BiData({
                    evid: 356,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviation.deviationId,
                    social_share: 'da_post',
                    deviationid: deviation.deviationId
                })
            },
            elementPlacementMap.report && {
                value: 'report',
                label: t('actionbar.report'),
                biData: BiData({
                    evid: 20,
                    modal_version: 2,
                    deviationid: deviation.deviationId
                }),
                onClick: ()=>{
                    showReportModal(deviation);
                    closeTooltip();
                }
            },
            elementPlacementMap.unpublish && {
                value: 'unpublish',
                label: t('actionbar.unpublish'),
                onClick: handleUnpublishClick
            }
        ]
    });
};
MoreMenuContent.displayName = 'MoreMenuContent';
export default MoreMenuContent;

//# sourceMappingURL=MoreMenuContent.js.map